import { type ClickEvent, MenuItem } from '@szhsin/react-menu';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../../../../shared/Locator';
import { getFetcherSettingsUrl } from '../../../../../../../shared/settings';
import { isBusinessTier, isEnterpriseTier } from '../../../../../../../shared/subscriptions';
import { window } from '../../../../../../globals';
import * as DependenciesV2Store from '../../../../../../redux/dependenciesV2';
import PaidFeatureGate from '../../../../../generic/Gates/PaidFeatureGate';
import { NEW_TAB_PROPS } from '../../../../../generic/Link';
import { DOCS_URL } from '../../../../../generic/Link/DocsLink';
import type { DependencyV2Modals } from '../../../hooks/useDependencyModals';

const QUEUE_BUILD = 'queue-build';
const VIEW_BUILD = 'view-build';
const EDIT_PACKAGE = 'edit-package';
const MANAGE_LABELS = 'manage-labels';
const OVERWRITE_PACKAGE = 'overwrite-package';
const IGNORE_PACKAGE = 'ignore-package';
const ADD_AUTHENTICATION = 'add-authentication';
const COPY_LOCATOR = 'copy-locator';

const stopPropagation = (e: ClickEvent) => {
  // ReactMenu's MenuItem uses a custom ClickEvent type that doesn't directly expose the stopPropagation method
  // so we need to access the synthetic event and call stopPropagation on it.
  e.syntheticEvent.stopPropagation();
};

type MenuItemProps = {
  modals: DependencyV2Modals;
  locator: string;
};

type IgnorePackageProps = {
  title: string;
  isManual: boolean;
} & MenuItemProps;

export const IgnorePackage: VoidFunctionComponent<IgnorePackageProps> = ({ modals, locator, title, isManual }) => {
  return (
    <MenuItem
      className="fossa-menu__item-danger"
      key={IGNORE_PACKAGE}
      onClick={(e) => {
        modals.deleteDependencyModal.onOpen({ context: { title, revision: locator, isManual } });
        stopPropagation(e);
      }}
    >
      {isManual ? 'Delete Package' : 'Ignore package'}
    </MenuItem>
  );
};

type QueueBuildProps = Omit<MenuItemProps, 'modals'>;

const analyzingStatus = {
  error: '',
  resolved: false,
  unsupported: false,
  analyzing: true,
};

export const QueueBuild: VoidFunctionComponent<QueueBuildProps> = ({ locator }) => {
  const dispatch = useDispatch();
  const { status } = useSelector(DependenciesV2Store.selectors.getDependency(locator)) || {};

  const queueBuild = async () => {
    // Update the dependency to an analyzing state so that it quickly updates when the user makes the build action
    dispatch(DependenciesV2Store.actions.updateDependency(locator, { status: analyzingStatus }));
    // Queue the build and add to the build watch
    try {
      await dispatch(DependenciesV2Store.actions.buildDependency(locator, true));
      await dispatch(DependenciesV2Store.actions.addAndFetchBuildWatchRevision(locator, true));
    } catch (error) {
      // If the build fails to queue revert the dependency back to its original status state
      dispatch(DependenciesV2Store.actions.updateDependency(locator, { status }));
    }
  };

  return (
    <MenuItem
      key={QUEUE_BUILD}
      onClick={(e) => {
        queueBuild();
        stopPropagation(e);
      }}
    >
      Queue build
    </MenuItem>
  );
};

type ViewBuildProps = Omit<MenuItemProps, 'locator'> & {
  buildId: number;
};

export const ViewBuild: VoidFunctionComponent<ViewBuildProps> = ({ modals, buildId }) => (
  <MenuItem
    key={VIEW_BUILD}
    onClick={(e) => {
      modals.streamBuildModal.onOpen({ context: { buildId } });
      stopPropagation(e);
    }}
  >
    View build
  </MenuItem>
);

export const EditPackage: VoidFunctionComponent<MenuItemProps> = ({ modals, locator }) => (
  <MenuItem
    key={EDIT_PACKAGE}
    onClick={(e) => {
      modals.editDependencyModal.onOpen({ context: { revision: locator } });
      stopPropagation(e);
    }}
  >
    Edit package
  </MenuItem>
);

export const OverwritePackage: VoidFunctionComponent<MenuItemProps> = ({ modals, locator }) => (
  <MenuItem
    key={OVERWRITE_PACKAGE}
    onClick={(e) => {
      modals.addDependencyModal.onOpen({ context: { mode: 'replace', dependencyToOverwrite: locator } });
      stopPropagation(e);
    }}
  >
    Overwrite package
  </MenuItem>
);

type AddAuthenticationProps = Omit<MenuItemProps, 'modals'>;

export const AddAuthentication: VoidFunctionComponent<AddAuthenticationProps> = ({ locator }) => {
  const { fetcher } = Locator.parse(locator);
  const fetcherSettingsUrl = getFetcherSettingsUrl(fetcher) || `${DOCS_URL}/supported-languages`;

  return (
    <MenuItem
      key={ADD_AUTHENTICATION}
      onClick={(e) => {
        stopPropagation(e);
      }}
      href={fetcherSettingsUrl}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...NEW_TAB_PROPS}
    >
      Add authentication
    </MenuItem>
  );
};

type ManageLabelsProps = MenuItemProps & {
  title: string;
};

export const ManageLabels: VoidFunctionComponent<ManageLabelsProps> = ({ modals, locator, title }) => {
  const plan = window.user?.organization?.stripe_plan;

  return (
    <PaidFeatureGate isRestricted={!(isBusinessTier({ plan }) || isEnterpriseTier({ plan }))} hidePill>
      <MenuItem
        key={MANAGE_LABELS}
        onClick={(e) => {
          modals.managePackageLabelsModal.onOpen({ context: { revision: locator, title } });
          stopPropagation(e);
        }}
        >
        Manage Labels
      </MenuItem>
    </PaidFeatureGate>
  );
};

export const CopyLocator: VoidFunctionComponent<MenuItemProps> = ({ locator }) => {
  return (
    <MenuItem
      key={COPY_LOCATOR}
      onClick={(e) => {
        navigator.clipboard.writeText(locator);
        stopPropagation(e);
      }}
    >
      Copy locator
    </MenuItem>
  );
};
